import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Copy, Info } from 'lucide-react';
import { supabaseClient } from '../../lib/supabase';
import { LoadingScreen } from '../common/LoadingScreen';
import { toast } from '../../lib/toast';

interface Prompt {
  id: string;
  name: string;
  description: string;
  content: string;
  category: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

const CATEGORIES = [
  { id: 'content_structure', name: 'Content Structure' },
  { id: 'content_generation', name: 'Content Generation' },
  { id: 'keyword_optimization', name: 'Keyword Optimization' },
  { id: 'content_enhancement', name: 'Content Enhancement' },
  { id: 'custom', name: 'Custom' }
];

const SNIPPETS = [
  { name: '{{Missing words}}', description: 'Missing keywords from Keywords tab' },
  { name: '{{Present words}}', description: 'Present keywords from Keywords tab' },
  { name: '{{Keywords}}', description: 'SEO Keywords from Keywords tab' },
  { name: '{{Headers}}', description: 'Headers with competitor domains from Competitors tab' },
  { name: '{{Text}}', description: 'Text from editor' },
  { name: '{{Resultai}}', description: 'Text from response field' },
  { name: '{{Description}}', description: 'Company description from project settings' },
  { name: '{{Language}}', description: '🆕 Content language (uk, ru, en) - AUTO' },
  { name: '{{Keyword}}', description: '🆕 Main keyword - AUTO' },
  { name: '{{Intent}}', description: '🆕 Search intent (commercial, informational, navigational, transactional) - AUTO' },
  { name: '{{ContentType}}', description: '🆕 Content type (category, blog-best, blog-how-to, blog-guide, etc.) - AUTO' },
  { name: '{{StructureRecommendations}}', description: '🆕 AI-generated structure recommendations based on content type - AUTO' },
  { name: '{{CompetitorInsights}}', description: '🆕 Insights from SERP competitors analysis - AUTO' }
];

const DEFAULT_PROMPTS = [
  {
    name: 'Generate Headers Structure',
    description: 'Create headers structure based on competitor headers and keywords',
    category: 'content_structure',
    content: `Зроби структуру заголовків для сторінки на основі Заголовків конкурентів [Headers] ключових слів [Keywords] та додай CTA за прикладом [Text]:

[Headers]:
{{Headers}}
[Keywords]:
{{Keywords}}
[Text]:
{{Text}}`
  },
  {
    name: 'Generate Content from Headers',
    description: 'Generate content based on headers structure and company info',
    category: 'content_generation',
    content: `Зроби текст на основі структури заголовків [Headers] та інформації про компанію [Company info]:
[Headers]:
{{Text}}
[Company info]:
{{Description}}`
  },
  {
    name: 'Enhance Content with Keywords',
    description: 'Add missing keywords to existing content',
    category: 'keyword_optimization',
    content: `Додай в поточний текст [Text] наступними словами [Missing words] не змінючи його, тільки доповнюючи заголовки, абзаци речення для того щоб збільшити к-сть слів до []. Виділи доповнення щоб було видно що саме додавалось.
[Text]:
{{Text}}
[Missing words]:
{{Missing words}}`
  },
  {
    name: 'Generate FAQ Section',
    description: 'Create FAQ section based on content',
    category: 'content_enhancement',
    content: `Зроби 5 FAQ (питання і відповіді) для текту [Text]:
[Text]:
{{Text}}`
  }
];

export default function PromptsPage() {
  const [loading, setLoading] = useState(true);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newPrompt, setNewPrompt] = useState<Partial<Prompt>>({
    name: '',
    description: '',
    content: '',
    category: 'custom',
    is_default: false
  });
  const [showSnippets, setShowSnippets] = useState(false);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseClient
        .from('prompts')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;

      if (!data || data.length === 0) {
        await createDefaultPrompts();
        return;
      }

      setPrompts(data);
    } catch (err) {
      console.error('Error loading prompts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const createDefaultPrompts = async () => {
    try {
      const { error } = await supabaseClient
        .from('prompts')
        .insert(DEFAULT_PROMPTS.map(prompt => ({ ...prompt, is_default: false })));

      if (error) throw error;

      await loadPrompts();
    } catch (err) {
      console.error('Error creating default prompts:', err);
      setError(err instanceof Error ? err.message : 'Failed to create default prompts');
    }
  };

  const handleSave = async (prompt: Partial<Prompt>) => {
    try {
      if (!prompt.name || !prompt.content || !prompt.category) {
        throw new Error('Please fill in all required fields');
      }

      if (prompt.id) {
        const { error: updateError } = await supabaseClient
          .from('prompts')
          .update({
            name: prompt.name,
            description: prompt.description,
            content: prompt.content,
            category: prompt.category
          })
          .eq('id', prompt.id);

        if (updateError) throw updateError;
        toast.success('Prompt updated successfully');
      } else {
        const { error: createError } = await supabaseClient
          .from('prompts')
          .insert([{ ...prompt, is_default: false }]);

        if (createError) throw createError;
        toast.success('Prompt created successfully');
      }

      setEditingPrompt(null);
      setIsCreating(false);
      setNewPrompt({
        name: '',
        description: '',
        content: '',
        category: 'custom',
        is_default: false
      });
      await loadPrompts();
    } catch (err) {
      console.error('Error saving prompt:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to save prompt');
    }
  };

  const handleDelete = async (promptId: string) => {
    try {
      const prompt = prompts.find(p => p.id === promptId);
      if (!prompt) return;

      if (prompt.is_default) {
        toast.error('Cannot delete default prompts');
        return;
      }

      const confirmed = window.confirm('Are you sure you want to delete this prompt?');
      if (!confirmed) return;

      const { error } = await supabaseClient
        .from('prompts')
        .delete()
        .eq('id', promptId);

      if (error) throw error;

      toast.success('Prompt deleted successfully');
      await loadPrompts();
    } catch (err) {
      console.error('Error deleting prompt:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete prompt');
    }
  };

  const handleCopy = async (prompt: Prompt) => {
    try {
      const newPromptData = {
        name: `${prompt.name} (Copy)`,
        description: prompt.description,
        content: prompt.content,
        category: prompt.category,
        is_default: false
      };

      const { error } = await supabaseClient
        .from('prompts')
        .insert([newPromptData]);

      if (error) throw error;

      toast.success('Prompt copied successfully');
      await loadPrompts();
    } catch (err) {
      console.error('Error copying prompt:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to copy prompt');
    }
  };

  const insertSnippet = (promptId: string | null, snippet: { name: string }) => {
    if (promptId) {
      const updatedPrompts = prompts.map(p => {
        if (p.id === promptId) {
          return {
            ...p,
            content: p.content + '\n' + snippet.name
          };
        }
        return p;
      });
      setPrompts(updatedPrompts);
    } else {
      setNewPrompt({
        ...newPrompt,
        content: (newPrompt.content || '') + '\n' + snippet.name
      });
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Prompts</h2>
          <button
            onClick={() => setIsCreating(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Prompt
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <Info className="h-5 w-5 text-blue-500 mr-2" />
              <h3 className="text-sm font-medium text-blue-900">Available Snippets</h3>
            </div>
            <button
              onClick={() => setShowSnippets(!showSnippets)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showSnippets ? 'Hide' : 'Show'} Details
            </button>
          </div>
          {showSnippets && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
              {SNIPPETS.map((snippet) => (
                <div key={snippet.name} className="flex items-start space-x-2 text-sm">
                  <code className="px-2 py-1 bg-blue-100 rounded text-blue-800 font-mono">
                    {snippet.name}
                  </code>
                  <span className="text-blue-700">-</span>
                  <span className="text-blue-800 flex-1">{snippet.description}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {isCreating && (
          <div className="mb-6 border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">New Prompt</h3>
              <button
                onClick={() => setIsCreating(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name *
                </label>
                <input
                  type="text"
                  value={newPrompt.name}
                  onChange={(e) => setNewPrompt({ ...newPrompt, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <input
                  type="text"
                  value={newPrompt.description}
                  onChange={(e) => setNewPrompt({ ...newPrompt, description: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Category *
                </label>
                <select
                  value={newPrompt.category}
                  onChange={(e) => setNewPrompt({ ...newPrompt, category: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  {CATEGORIES.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Content *
                </label>
                <div className="mt-1 relative">
                  <textarea
                    value={newPrompt.content}
                    onChange={(e) => setNewPrompt({ ...newPrompt, content: e.target.value })}
                    rows={6}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono"
                  />
                  <div className="absolute right-2 top-2 space-x-2">
                    {SNIPPETS.map((snippet) => (
                      <button
                        key={snippet.name}
                        onClick={() => insertSnippet(null, snippet)}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                        title={snippet.description}
                      >
                        {snippet.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSave(newPrompt)}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {CATEGORIES.map((category) => {
            const categoryPrompts = prompts.filter(p => p.category === category.id);
            if (categoryPrompts.length === 0) return null;

            return (
              <div key={category.id}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {category.name}
                </h3>
                <div className="space-y-4">
                  {categoryPrompts.map((prompt) => (
                    <div
                      key={prompt.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-indigo-200 transition-colors duration-200"
                    >
                      <div className="flex justify-between items-center mb-4 bg-gray-50 p-4 rounded-lg">
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">
                            {prompt.name}
                            {prompt.is_default && (
                              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                                Default
                              </span>
                            )}
                          </h4>
                          {prompt.description && (
                            <p className="text-sm text-gray-500 mt-1">
                              {prompt.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => setEditingPrompt(prompt.id)}
                            className="flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                          >
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleCopy(prompt)}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200"
                            title="Copy prompt"
                          >
                            <Copy className="h-5 w-5" />
                          </button>
                        </div>
                      </div>

                      {editingPrompt === prompt.id ? (
                        <div className="space-y-4 mt-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Name
                            </label>
                            <input
                              type="text"
                              value={prompt.name}
                              onChange={(e) => {
                                const updatedPrompts = prompts.map(p =>
                                  p.id === prompt.id ? { ...p, name: e.target.value } : p
                                );
                                setPrompts(updatedPrompts);
                              }}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Description
                            </label>
                            <input
                              type="text"
                              value={prompt.description || ''}
                              onChange={(e) => {
                                const updatedPrompts = prompts.map(p =>
                                  p.id === prompt.id ? { ...p, description: e.target.value } : p
                                );
                                setPrompts(updatedPrompts);
                              }}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Category
                            </label>
                            <select
                              value={prompt.category}
                              onChange={(e) => {
                                const updatedPrompts = prompts.map(p =>
                                  p.id === prompt.id ? { ...p, category: e.target.value } : p
                                );
                                setPrompts(updatedPrompts);
                              }}
                              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            >
                              {CATEGORIES.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Content
                            </label>
                            <div className="mt-1 relative">
                              <textarea
                                value={prompt.content}
                                onChange={(e) => {
                                  const updatedPrompts = prompts.map(p =>
                                    p.id === prompt.id ? { ...p, content: e.target.value } : p
                                  );
                                  setPrompts(updatedPrompts);
                                }}
                                rows={6}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono"
                              />
                              <div className="absolute right-2 top-2 space-x-2">
                                {SNIPPETS.map((snippet) => (
                                  <button
                                    key={snippet.name}
                                    onClick={() => insertSnippet(prompt.id, snippet)}
                                    className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                                    title={snippet.description}
                                  >
                                    {snippet.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="flex justify-end space-x-3">
                            <button
                              onClick={() => setEditingPrompt(null)}
                              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleDelete(prompt.id)}
                              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              Delete
                            </button>
                            <button
                              onClick={() => handleSave(prompt)}
                              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                              Save Changes
                            </button>
                          </div>
                        </div>
                      ) : (
                        <pre className="mt-2 whitespace-pre-wrap text-sm text-gray-600 font-mono bg-gray-50 p-4 rounded-md">
                          {prompt.content}
                        </pre>
                      )}

                      <div className="mt-2 text-xs text-gray-500">
                        Last updated: {new Date(prompt.updated_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}